import { useCallback, useMemo } from 'react'
import type { CrudFilter } from '@refinedev/core'

import type { SettlementStatusRoot } from '@repo/sdk'

import { useSyncTableWithUrl } from '../../shared/hooks/useSyncTableWithUrl'

export function buildSettlementHistoryFilters(
  status: SettlementStatusRoot | '',
  dateFrom: string,
  dateTo: string,
): CrudFilter[] {
  const filters: CrudFilter[] = []
  if (status) filters.push({ field: 'status', operator: 'eq', value: status })
  if (dateFrom) filters.push({ field: 'dateFrom', operator: 'eq', value: dateFrom })
  if (dateTo) filters.push({ field: 'dateTo', operator: 'eq', value: dateTo })
  return filters
}

interface UseSettlementHistoryFiltersParams {
  /** Filter values read from the URL (the single source of truth). */
  status: SettlementStatusRoot | ''
  dateFrom: string
  dateTo: string
  urlPage: number
  currentPage: number
  setFilters: (filters: CrudFilter[], behavior: 'replace') => void
  setCurrentPage: (page: number) => void
  refetch: () => void
}

/** Pushes the URL-derived filters into refine's table and exposes a working refresh. */
export function useSettlementHistoryFilters({
  status,
  dateFrom,
  dateTo,
  urlPage,
  currentPage,
  setFilters,
  setCurrentPage,
  refetch,
}: UseSettlementHistoryFiltersParams) {
  const filters = useMemo(
    () => buildSettlementHistoryFilters(status, dateFrom, dateTo),
    [status, dateFrom, dateTo],
  )
  useSyncTableWithUrl({ filters, urlPage, currentPage, setFilters, setCurrentPage })

  // Re-setting identical filters does not change refine's content-hashed query key,
  // so the old "refresh" never refetched anything.
  const handleRefresh = useCallback(() => refetch(), [refetch])

  return { handleRefresh } as const
}
